import { ReactNode } from "react";
import { Box } from "@mantine/core";
import { IconLock, IconArrowRight } from "@tabler/icons-react";
import { Link } from "@inertiajs/react";
import { L2KH2, L2KText, L2KCaption } from "../ui/Typography";

interface Props {
  children: ReactNode;
  eyebrow?: string;
  title?: string;
  description?: string;
  ctaLabel?: string;
  ctaHref?: string;
}

export function ComingSoonPageOverlay({
  children,
  eyebrow = "Rehearsal in progress",
  title = "2.0 Launching Soon",
  description = "The full video catalog is being staged for the 2.0 launch. In the meantime, catch the story behind the moves in our latest posts.",
  ctaLabel = "Read the posts",
  ctaHref = "/posts",
}: Props) {
  return (
    <Box
      style={{
        position: "relative",
        isolation: "isolate",
        height: "calc(100dvh - 100px)",
        minHeight: 560,
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden
        style={{
          filter: "blur(14px) brightness(0.32) saturate(0.7)",
          pointerEvents: "none",
          userSelect: "none",
          transform: "scale(1.02)",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {children}
      </Box>

      <Box
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "clamp(24px, 4vw, 64px)",
          zIndex: 2,
        }}
      >
        <Box
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(10,10,10,0.65) 65%, rgba(0,0,0,0.9) 100%)",
            pointerEvents: "none",
          }}
        />

        <Box
          style={{
            position: "absolute",
            top: 0,
            left: "18%",
            width: "clamp(140px, 22vw, 320px)",
            height: "100%",
            background:
              "linear-gradient(180deg, rgba(255,152,0,0.28) 0%, rgba(255,152,0,0.08) 35%, transparent 75%)",
            transform: "skewX(-8deg)",
            transformOrigin: "top center",
            filter: "blur(14px)",
            pointerEvents: "none",
            animation: "l2k-beam-glow 6s ease-in-out infinite alternate",
          }}
        />
        <Box
          style={{
            position: "absolute",
            top: 0,
            right: "16%",
            width: "clamp(120px, 18vw, 280px)",
            height: "100%",
            background:
              "linear-gradient(180deg, rgba(255,193,7,0.22) 0%, rgba(255,193,7,0.06) 40%, transparent 78%)",
            transform: "skewX(10deg)",
            transformOrigin: "top center",
            filter: "blur(18px)",
            pointerEvents: "none",
            animation:
              "l2k-beam-glow 7.5s ease-in-out infinite alternate-reverse",
          }}
        />

        <Box
          style={{
            position: "relative",
            maxWidth: 640,
            width: "100%",
            padding: "clamp(28px, 4vw, 56px)",
            background:
              "linear-gradient(180deg, rgba(23,23,23,0.68) 0%, rgba(15,15,15,0.88) 100%)",
            backdropFilter: "blur(24px) saturate(1.2)",
            border: "1px solid rgba(255, 152, 0, 0.22)",
            borderRadius: 2,
            boxShadow:
              "0 40px 80px -30px rgba(0,0,0,0.9), inset 0 0 0 1px rgba(255,255,255,0.02)",
            animation:
              "l2k-panel-in 800ms cubic-bezier(0.16, 1, 0.3, 1) both",
            overflow: "hidden",
          }}
        >
          <CornerTicks />

          <Box
            aria-hidden
            style={{
              position: "absolute",
              right: "-3%",
              top: "-18%",
              fontSize: "clamp(180px, 22vw, 320px)",
              fontWeight: 900,
              fontFamily: "'Public Sans', sans-serif",
              lineHeight: 1,
              letterSpacing: "-0.06em",
              color: "transparent",
              WebkitTextStroke: "1px rgba(255, 152, 0, 0.14)",
              userSelect: "none",
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            2.0
          </Box>

          <Box style={{ position: "relative", zIndex: 1 }}>
            <Box
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                marginBottom: "clamp(20px, 3vw, 32px)",
                animation: "l2k-fade-up 700ms 100ms both",
              }}
            >
              <Box style={{ width: 24, height: 1, background: "#FF9800" }} />
              <L2KCaption
                style={{
                  color: "#FF9800",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  margin: 0,
                }}
              >
                {eyebrow}
              </L2KCaption>
            </Box>

            <Box
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "clamp(16px, 2vw, 24px)",
                marginBottom: "clamp(16px, 2vw, 24px)",
                animation: "l2k-fade-up 700ms 200ms both",
              }}
            >
              <Box
                style={{
                  flexShrink: 0,
                  width: "clamp(52px, 6vw, 68px)",
                  height: "clamp(52px, 6vw, 68px)",
                  borderRadius: "50%",
                  background:
                    "radial-gradient(circle at 30% 30%, rgba(255, 193, 7, 0.32), rgba(255, 152, 0, 0.08))",
                  border: "1px solid rgba(255, 152, 0, 0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation: "l2k-lock-pulse 3.6s ease-in-out infinite",
                }}
              >
                <IconLock size={26} color="#FF9800" strokeWidth={1.8} />
              </Box>
              <L2KH2
                style={{
                  margin: 0,
                  fontSize: "clamp(28px, 4.2vw, 44px)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                }}
              >
                {title}
              </L2KH2>
            </Box>

            <L2KText
              variant="secondary"
              style={{
                fontSize: "clamp(15px, 1.4vw, 17px)",
                lineHeight: 1.6,
                maxWidth: "42ch",
                margin: 0,
                marginBottom: "clamp(24px, 3vw, 36px)",
                animation: "l2k-fade-up 700ms 300ms both",
              }}
            >
              {description}
            </L2KText>

            <Link
              href={ctaHref}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "14px 22px 14px 24px",
                background:
                  "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
                color: "#0f0f0f",
                fontFamily: "'Public Sans', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                textDecoration: "none",
                borderRadius: 2,
                boxShadow:
                  "0 12px 24px -8px rgba(255, 152, 0, 0.5), inset 0 -2px 0 rgba(0,0,0,0.15)",
                transition:
                  "transform 0.2s ease, box-shadow 0.2s ease",
                animation: "l2k-fade-up 700ms 400ms both",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 16px 32px -8px rgba(255, 152, 0, 0.65), inset 0 -2px 0 rgba(0,0,0,0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 12px 24px -8px rgba(255, 152, 0, 0.5), inset 0 -2px 0 rgba(0,0,0,0.15)";
              }}
            >
              {ctaLabel}
              <IconArrowRight size={16} strokeWidth={2.5} />
            </Link>
          </Box>
        </Box>
      </Box>

      <style>{`
        @keyframes l2k-panel-in {
          0% { opacity: 0; transform: translateY(24px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes l2k-fade-up {
          0% { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes l2k-lock-pulse {
          0%, 100% { box-shadow: 0 0 24px rgba(255,152,0,0.25), 0 0 0 6px rgba(255,152,0,0.06); }
          50%     { box-shadow: 0 0 40px rgba(255,152,0,0.44), 0 0 0 10px rgba(255,152,0,0.08); }
        }
        @keyframes l2k-beam-glow {
          0%   { opacity: 0.55; }
          100% { opacity: 0.95; }
        }
      `}</style>
    </Box>
  );
}

function CornerTicks() {
  const base = {
    position: "absolute" as const,
    width: 18,
    height: 18,
    borderColor: "rgba(255, 152, 0, 0.55)",
    borderStyle: "solid" as const,
    borderWidth: 0,
    pointerEvents: "none" as const,
    zIndex: 2,
  };
  return (
    <>
      <Box style={{ ...base, top: 10, left: 10, borderTopWidth: 1, borderLeftWidth: 1 }} />
      <Box style={{ ...base, top: 10, right: 10, borderTopWidth: 1, borderRightWidth: 1 }} />
      <Box style={{ ...base, bottom: 10, left: 10, borderBottomWidth: 1, borderLeftWidth: 1 }} />
      <Box style={{ ...base, bottom: 10, right: 10, borderBottomWidth: 1, borderRightWidth: 1 }} />
    </>
  );
}

export default ComingSoonPageOverlay;
